/**
 * workoutOutbox — fila persistente de sincronização do treino
 *
 * Problema que resolve (achado P0 da revisão mobile de 06/09/2026):
 * o salvamento de série era um PATCH direto com `.catch(console.error)`.
 * Na academia — subsolo, vestiário, sinal fraco — a requisição falhava,
 * a série aparecia marcada na tela (estado local), mas NUNCA chegava no
 * servidor e nada avisava o aluno. Pior: o fechamento automático da
 * sessão no backend (auto_close_expired_session) preenchia o que faltava
 * com zeros, e o trabalho real virava zero.
 *
 * Como funciona:
 *   - Toda escrita entra numa fila persistida em localStorage ANTES de ir
 *     pra rede — sobrevive a fechar o app, travar o navegador, ficar sem
 *     bateria.
 *   - Uma entrada por (sessão, exercício, tipo): o payload é sempre o
 *     estado completo das séries daquele exercício, então last-write-wins
 *     é correto e a fila não cresce indefinidamente.
 *   - Reenvio com backoff exponencial, disparado por: enfileiramento,
 *     volta da conexão (evento `online`), app voltar ao primeiro plano
 *     (visibilitychange), boot do app e um tick periódico.
 *   - Erro definitivo (sessão não existe mais, sem permissão) descarta a
 *     entrada em vez de tentar pra sempre.
 *
 * O backend faz a outra metade: `PATCH /items/progress` e `POST /items`
 * reconciliam registro real que chega atrasado por cima dos zeros
 * sintéticos do fechamento automático — ver _merge_auto_filled_sets em
 * app/api/workout_sessions.py.
 */
import api from "./api";

const STORAGE_KEY = "workout_outbox_v1";

// Backoff por tentativa (ms). A partir da última posição, repete o teto.
const BACKOFF_MS = [2000, 5000, 15000, 30000, 60000, 120000, 300000];

// Teto de tentativas antes de desistir — ~1h30 de tentativas no total.
const MAX_ATTEMPTS = 20;

// Tick de verificação enquanto houver algo pendente.
const TICK_MS = 10000;

let queue = [];
let flushing = false;
let tickTimer = null;
const listeners = new Set();

// ── Persistência ─────────────────────────────────────────────────────────
function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    queue = Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    queue = [];
  }
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch (_) {
    // Cota estourada / modo privado — a fila continua valendo em memória
    // nesta sessão do app; não há o que fazer além de seguir.
  }
}

// ── Status pra UI ────────────────────────────────────────────────────────
function snapshot() {
  return {
    pending: queue.length,
    syncing: flushing,
    // true quando já houve pelo menos uma falha de rede numa entrada ainda
    // na fila — é o que a UI usa pra dizer "pendente" em vez de "salvando".
    failing: queue.some((e) => (e.attempts || 0) > 0),
  };
}

function emit() {
  const s = snapshot();
  listeners.forEach((cb) => {
    try { cb(s); } catch (_) {}
  });
}

export function subscribe(cb) {
  listeners.add(cb);
  try { cb(snapshot()); } catch (_) {}
  return () => listeners.delete(cb);
}

export function getStatus() {
  return snapshot();
}

// ── Enfileiramento ───────────────────────────────────────────────────────
function upsert(entry) {
  const key = `${entry.kind}:${entry.sessionId}:${entry.itemId}`;
  const idx = queue.findIndex(
    (e) => `${e.kind}:${e.sessionId}:${e.itemId}` === key
  );
  if (idx >= 0) {
    // Substitui o payload pelo mais recente, mas preserva o histórico de
    // tentativas pra não reiniciar o backoff a cada nova série digitada
    // com a rede caída.
    queue[idx] = {
      ...queue[idx],
      payload: entry.payload,
      updatedAt: Date.now(),
    };
  } else {
    queue.push({
      ...entry,
      attempts: 0,
      nextAttemptAt: 0,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
  }
  persist();
  emit();
  scheduleTick();
  flush();
}

/** Progresso parcial de um exercício (séries ainda em andamento). */
export function enqueueProgress(sessionId, itemId, setsLog, edit = false) {
  if (!sessionId || !itemId) return;
  upsert({
    kind: "progress",
    sessionId,
    itemId,
    // edit: exercício já concluído e o aluno corrigiu reps/carga (o servidor
    // só aplica a correção quando a flag vem).
    payload: {
      user_workout_plan_item_id: itemId,
      sets_log: setsLog,
      ...(edit ? { edit: true } : {}),
    },
  });
}

/** Conclusão de um exercício (usado como retry quando o POST direto falha). */
export function enqueueComplete(sessionId, itemId, payload) {
  if (!sessionId || !itemId) return;
  upsert({ kind: "complete", sessionId, itemId, payload });
}

// ── Envio ────────────────────────────────────────────────────────────────
function backoffFor(attempts) {
  return BACKOFF_MS[Math.min(attempts, BACKOFF_MS.length - 1)];
}

async function send(entry) {
  if (entry.kind === "progress") {
    return api.patch(
      `/workout-sessions/${entry.sessionId}/items/progress`,
      entry.payload
    );
  }
  return api.post(`/workout-sessions/${entry.sessionId}/items`, entry.payload);
}

/**
 * Erros que NÃO adianta reenviar: a sessão sumiu, o item sumiu, ou o
 * usuário não tem permissão. Reenviar em loop só gastaria bateria e dados.
 * 401 fica de fora de propósito — o interceptor do api.js renova o token e
 * a próxima tentativa passa.
 */
function isPermanent(status) {
  return status === 400 || status === 403 || status === 404 || status === 422;
}

export async function flush() {
  if (flushing) return;
  if (!queue.length) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;

  flushing = true;
  emit();

  const now = Date.now();
  const due = queue.filter((e) => (e.nextAttemptAt || 0) <= now);

  for (const entry of due) {
    // A entrada pode ter sido removida/substituída por um flush concorrente
    if (!queue.includes(entry)) continue;
    try {
      await send(entry);
      queue = queue.filter((e) => e !== entry);
      persist();
      emit();
    } catch (err) {
      const status = err?.response?.status;
      if (status && isPermanent(status)) {
        console.warn(
          "[outbox] descartando entrada irrecuperável:",
          entry.kind, entry.sessionId, entry.itemId, status
        );
        queue = queue.filter((e) => e !== entry);
        persist();
        emit();
        continue;
      }
      entry.attempts = (entry.attempts || 0) + 1;
      if (entry.attempts >= MAX_ATTEMPTS) {
        console.error(
          "[outbox] desistindo após", entry.attempts, "tentativas:",
          entry.kind, entry.sessionId, entry.itemId
        );
        queue = queue.filter((e) => e !== entry);
      } else {
        entry.nextAttemptAt = Date.now() + backoffFor(entry.attempts);
      }
      persist();
      emit();
    }
  }

  flushing = false;
  emit();
  scheduleTick();
}

// ── Gatilhos automáticos ─────────────────────────────────────────────────
function scheduleTick() {
  if (tickTimer) {
    clearTimeout(tickTimer);
    tickTimer = null;
  }
  if (!queue.length) return;
  tickTimer = setTimeout(() => {
    tickTimer = null;
    flush();
  }, TICK_MS);
}

let started = false;

/** Chamado uma vez no boot do app (App.js). */
export function startOutbox() {
  if (started) return;
  started = true;
  load();
  emit();

  if (typeof window !== "undefined") {
    window.addEventListener("online", flush);
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) flush();
    });
  }

  // Tenta esvaziar o que ficou preso da sessão anterior do app.
  flush();
  scheduleTick();
}

/** Só pra teste/diagnóstico. */
export function _debugQueue() {
  return queue.slice();
}
